import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isAxiosError } from "axios";
import { z } from "zod";
import {
  CreatePatientSchema,
  UpdatePatientSchema,
  UserRole,
  Gender,
} from "@arna-med/shared";
import { useSession } from "@/lib/auth-client";
import { useAuth } from "@/contexts/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2, Share2, X, Pencil, Loader2, AlertTriangle, Search, MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  usePatient,
  usePatientSearch,
  useCreatePatient,
  useUpdatePatient,
  useDeletePatient,
  useSharePatient,
  useRevokePatientShare,
  type PatientSearchItem,
} from "@/hooks/api/usePatients";
import { useDoctors } from "@/hooks/api/useUsers";

interface ExistingPatientConflict {
  id: string;
  name: string;
  personalNumber: string;
}

type CreatePatientForm = z.infer<typeof CreatePatientSchema>;
type UpdatePatientForm = z.infer<typeof UpdatePatientSchema>;

export default function AdminPatients() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: session } = useSession();
  const { profile } = useAuth();
  const currentRole = (session?.user as { role?: string } | undefined)?.role as
    | UserRole
    | undefined;
  const isOwner = currentRole === UserRole.Admin;
  const isDoctor = currentRole === UserRole.Doctor;

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showForm, setShowForm] = useState(() => searchParams.get("add") === "1");
  const [duplicateConflict, setDuplicateConflict] = useState<ExistingPatientConflict | null>(null);
  const [sharingPatientId, setSharingPatientId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<{
    patientId: string;
    doctorId: string;
  } | null>(null);
  const [editingPatientId, setEditingPatientId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<UpdatePatientForm>({});
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const [form, setForm] = useState<CreatePatientForm>({
    name: "",
    personalNumber: "",
    gender: Gender.Male,
  });

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const isDebouncing = searchInput.trim() !== searchQuery.trim() && searchInput.trim().length >= 1;
  const isSearching = searchQuery.trim().length >= 1;
  const { data: searchResults, isLoading: isSearchLoading } = usePatientSearch(searchQuery);
  const { data: patientDetail } = usePatient(sharingPatientId);
  const { data: doctors } = useDoctors({ enabled: !!sharingPatientId });
  const createMutation = useCreatePatient();
  const deleteMutation = useDeletePatient();
  const shareMutation = useSharePatient();
  const revokeMutation = useRevokePatientShare();
  const updateMutation = useUpdatePatient();

  const patients = isSearching ? (searchResults ?? []) : [];
  const sharingPatient = patients.find((p) => p.id === sharingPatientId);
  const editingPatient = patients.find((p) => p.id === editingPatientId);

  return (
    <Layout>
      <h1 className="text-2xl font-bold mb-4">{t("patients.title")}</h1>

      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            className="pl-9 pr-9"
            placeholder={t("patients.searchPlaceholder")}
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
            }}
          />
          {searchInput && (
            isDebouncing || isSearchLoading ? (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground pointer-events-none" />
            ) : (
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setSearchInput("");
                }}
              >
                <X className="h-4 w-4" />
              </button>
            )
          )}
        </div>
        <Button
          onClick={() => {
            setShowForm((v) => !v);
            setDuplicateConflict(null);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          {t("patients.addPatient")}
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">
              {t("patients.addPatient")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setDuplicateConflict(null);
                const parsed = CreatePatientSchema.safeParse(form);
                if (parsed.success)
                  createMutation.mutate(parsed.data, {
                    onSuccess: () => {
                      setShowForm(false);
                      setForm({ name: "", personalNumber: "", gender: Gender.Male });
                    },
                    onError: (err) => {
                      if (
                        isAxiosError(err) &&
                        err.response?.status === 409 &&
                        err.response.data?.errorCode === "PATIENT_ALREADY_EXISTS"
                      ) {
                        setDuplicateConflict(err.response.data.existingPatient);
                      }
                    },
                  });
              }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="space-y-2">
                <Label>{t("patients.fullName")} <span className="text-destructive">*</span></Label>
                <Input
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  placeholder={t("patients.fullNamePlaceholder")}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>{t("patients.personalNumber")} <span className="text-destructive">*</span></Label>
                <Input
                  value={form.personalNumber}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, personalNumber: e.target.value }))
                  }
                  placeholder={t("patients.personalNumberPlaceholder")}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>{t("patients.gender")} <span className="text-destructive">*</span></Label>
                <select
                  value={form.gender}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, gender: e.target.value as Gender }))
                  }
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  required
                >
                  <option value={Gender.Male}>{t("patients.male")}</option>
                  <option value={Gender.Female}>{t("patients.female")}</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>{t("patients.phone")}</Label>
                <Input
                  type="tel"
                  value={form.phone ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      phone: e.target.value || undefined,
                    }))
                  }
                  placeholder={t("patients.phonePlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("patients.email")}</Label>
                <Input
                  type="email"
                  value={form.email ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      email: e.target.value || undefined,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>{t("patients.dateOfBirth")}</Label>
                <Input
                  type="date"
                  value={form.dateOfBirth ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      dateOfBirth: e.target.value || undefined,
                    }))
                  }
                />
              </div>
              <div className="flex items-end justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setDuplicateConflict(null);
                    setForm({ name: "", personalNumber: "", gender: Gender.Male });
                  }}
                >
                  {t("common.cancel")}
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  {t("common.save")}
                </Button>
              </div>
            </form>
            {duplicateConflict && (
              <div className="mt-4 rounded-md border border-yellow-300 bg-yellow-50 p-3 flex flex-col gap-2">
                <div className="flex items-start gap-2 text-yellow-800 text-sm">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    {t("patients.duplicateIIN", {
                      iin: duplicateConflict.personalNumber,
                      name: duplicateConflict.name,
                    })}
                  </span>
                </div>
                <div className="flex gap-2 pl-6">
                  {isDoctor && profile && (
                    <Button
                      size="sm"
                      disabled={shareMutation.isPending}
                      onClick={() => {
                        shareMutation.mutate(
                          { patientId: duplicateConflict.id, doctorId: profile.id },
                          {
                            onSuccess: () => {
                              setShowForm(false);
                              setForm({ name: "", personalNumber: "", gender: Gender.Male });
                              setDuplicateConflict(null);
                            },
                          },
                        );
                      }}
                    >
                      {shareMutation.isPending && (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      )}
                      {t("patients.attachPatient")}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Share management panel */}
      {sharingPatientId && sharingPatient && (
        <Card className="mb-6 border-primary">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                {t("patients.manageSharing")} — {sharingPatient.name}
              </CardTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSharingPatientId(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium mb-2">
                {t("patients.sharedWith")}
              </p>
              {patientDetail?.shares.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t("patients.noShares")}
                </p>
              ) : (
                <div className="space-y-2">
                  {patientDetail?.shares.map((s) => (
                    <div
                      key={s.doctorId}
                      className="flex items-center justify-between text-sm"
                    >
                      <span>
                        {s.doctorName}{" "}
                        <span className="text-muted-foreground">
                          ({s.doctorEmail})
                        </span>
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setConfirmRevoke({
                            patientId: sharingPatientId,
                            doctorId: s.doctorId,
                          })
                        }
                        disabled={revokeMutation.isPending}
                      >
                        {t("patients.revokeShare")}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <select
                value={selectedDoctorId}
                onChange={(e) => setSelectedDoctorId(e.target.value)}
                className="flex-1 h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">{t("patients.addDoctor")}</option>
                {doctors
                  ?.filter(
                    (d) =>
                      !patientDetail?.shares.some((s) => s.doctorId === d.id),
                  )
                  .map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} ({d.email})
                    </option>
                  ))}
              </select>
              <Button
                onClick={() => {
                  if (selectedDoctorId) {
                    shareMutation.mutate(
                      { patientId: sharingPatientId, doctorId: selectedDoctorId },
                      { onSuccess: () => setSelectedDoctorId("") },
                    );
                  }
                }}
                disabled={!selectedDoctorId || shareMutation.isPending}
              >
                {t("patients.share")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit panel */}
      {editingPatientId && editingPatient && (
        <Card className="mb-6 border-primary">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                {t("common.edit")} — {editingPatient.name}
              </CardTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setEditingPatientId(null);
                  setEditForm({});
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateMutation.mutate(
                  { id: editingPatientId, data: editForm },
                  {
                    onSuccess: () => {
                      setEditingPatientId(null);
                      setEditForm({});
                    },
                  },
                );
              }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <div className="space-y-2">
                <Label>{t("patients.fullName")}</Label>
                <Input
                  value={editForm.name ?? editingPatient.name}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, name: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>{t("patients.personalNumber")}</Label>
                <Input
                  value={
                    editForm.personalNumber ?? editingPatient.personalNumber
                  }
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      personalNumber: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>{t("patients.gender")}</Label>
                <select
                  value={editForm.gender ?? editingPatient.gender}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      gender: e.target.value as Gender,
                    }))
                  }
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value={Gender.Male}>{t("patients.male")}</option>
                  <option value={Gender.Female}>{t("patients.female")}</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>{t("patients.phone")}</Label>
                <Input
                  type="tel"
                  value={editForm.phone ?? editingPatient.phone ?? ""}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      phone: e.target.value || undefined,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>{t("patients.email")}</Label>
                <Input
                  type="email"
                  value={editForm.email ?? editingPatient.email ?? ""}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      email: e.target.value || undefined,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>{t("patients.dateOfBirth")}</Label>
                <Input
                  type="date"
                  value={
                    editForm.dateOfBirth ?? editingPatient.dateOfBirth ?? ""
                  }
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      dateOfBirth: e.target.value || undefined,
                    }))
                  }
                />
              </div>
              <div className="flex items-end justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEditingPatientId(null);
                    setEditForm({});
                  }}
                >
                  {t("common.cancel")}
                </Button>
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  {t("common.save")}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {isSearching && (
        isSearchLoading ? (
          <Spinner />
        ) : (
        <>
          {patients.length === 0 && (
            <p className="text-sm text-muted-foreground py-4 text-center">
              {t("patients.noResults")}
            </p>
          )}
          <div className="rounded-lg border overflow-x-auto touch-pan-y">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground whitespace-nowrap">{t("patients.fullName")}</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground whitespace-nowrap">{t("patients.personalNumber")}</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground whitespace-nowrap">{t("patients.gender")}</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground whitespace-nowrap">{t("patients.dateOfBirthHeader")}</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {patients.map((p) => {
                  const notOwn = isDoctor && isSearching && (p as PatientSearchItem).isOwn === false;
                  return (
                    <tr
                      key={p.id}
                      className="border-b last:border-0 hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => navigate(`/patients/${p.id}`)}
                    >
                      <td className="px-4 py-3 font-medium">{p.name}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{p.personalNumber}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{t(`patients.${p.gender}`)}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{p.dateOfBirth ?? "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end">
                          {notOwn ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={shareMutation.isPending}
                              onClick={() => {
                                if (!profile) return;
                                shareMutation.mutate(
                                  { patientId: p.id, doctorId: profile.id },
                                  {
                                    onSuccess: () => {
                                      queryClient.invalidateQueries({ queryKey: ["patients-search"] });
                                      queryClient.invalidateQueries({ queryKey: ["patients"] });
                                    },
                                  },
                                );
                              }}
                            >
                              {shareMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                              {t("patients.attachPatient")}
                            </Button>
                          ) : (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="focus-visible:ring-0 focus-visible:ring-offset-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => {
                                    setEditingPatientId(p.id);
                                    setEditForm({});
                                    setSharingPatientId(null);
                                  }}
                                >
                                  <Pencil className="h-4 w-4" />
                                  {t("common.edit")}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => setSharingPatientId(sharingPatientId === p.id ? null : p.id)}
                                >
                                  <Share2 className="h-4 w-4" />
                                  {t("patients.share")}
                                </DropdownMenuItem>
                                {isOwner && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-destructive focus:text-destructive"
                                      onClick={() => setConfirmDeleteId(p.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                      {t("common.delete")}
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
        )
      )}
      <ConfirmDialog
        open={confirmDeleteId !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmDeleteId(null);
        }}
        onConfirm={() => {
          deleteMutation.mutate(confirmDeleteId!);
          setConfirmDeleteId(null);
        }}
        isPending={deleteMutation.isPending}
      />
      <ConfirmDialog
        open={confirmRevoke !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmRevoke(null);
        }}
        onConfirm={() => {
          revokeMutation.mutate(confirmRevoke!);
          setConfirmRevoke(null);
        }}
        isPending={revokeMutation.isPending}
      />
    </Layout>
  );
}
